import React, { useEffect } from 'react';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import PricingCard from './components/PricingCard';
import FeatureComparison from './components/FeatureComparison';
import FAQ from './components/FAQ';
import Seo from '../../components/Seo';
import ProPlusWaitlistForm from './components/ProPlusWaitlistForm';
import { appendTrackingParams } from '../../utils/tracking';
import { Link } from 'react-router-dom';

const SubscriptionPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Early Access gating (feature flag + cohort cap)
  const earlyAccessEnabled = ((import.meta.env.VITE_EARLY_ACCESS_ENABLED ?? 'true') !== 'false');
  const EARLY_ACCESS_CAP = Number(import.meta.env.VITE_EARLY_ACCESS_CAP ?? '150');
  const getEarlyAccessCurrent = () => {
    // Prefer local override for manual QA; fallback to env; default 0
    try {
      if (typeof window !== 'undefined' && window?.localStorage) {
        const raw = window.localStorage.getItem('neevii:early_access_count');
        const parsed = Number(raw);
        if (Number.isFinite(parsed)) return parsed;
      }
    } catch (_) {}
    const envVal = Number(import.meta.env.VITE_EARLY_ACCESS_CURRENT ?? '');
    return Number.isFinite(envVal) ? envVal : 0;
  };
  const EARLY_ACCESS_CURRENT = getEarlyAccessCurrent();
  const isEarlyAccessCapReached = EARLY_ACCESS_CURRENT >= EARLY_ACCESS_CAP;
  const showEarlyAccess = earlyAccessEnabled && !isEarlyAccessCapReached;

  const betaPlan = {
    id: 'beta',
    slug: 'beta',
    name: 'Early Access',
    description: 'Limited to the first 150 UK users — 14-day free, then £9/mo for 3 months.',
    persona: 'UK early adopters',
    price: '£9',
    priceSuffix: '/month',
    tagline: 'First 150 users • 14-day free • £9/mo for 3 months',
    badge: 'Early Access',
    includes: 'Full access to Pro tier features',
    features: [
      '14-day free trial',
      'Intro pricing for 3 months',
      'AI-assisted filings & invoicing',
      'Priority onboarding during beta'
    ],
    mobileFeatures: [
      '14-day free trial',
      '£9/mo for 3 months',
      'AI filings & invoicing',
      'Priority onboarding'
    ],
    notes: [
      'After 3 months, subscription transitions to a Standard tier.'
    ],
    cta: {
      type: 'start-trial',
      label: 'Join Early Access'
    }
  };

  const basePlans = [
    {
      id: 'starter',
      slug: 'starter',
      name: 'Free',
      subtitle: 'for Sole traders',
      description: 'Essentials for sole traders getting started with AI accounting.',
      persona: null,
      price: '£0',
      priceSuffix: '/month',
      tagline: 'Always free for sole traders.',
      includes: null,
      supportCopy: 'Free plan • No credit card required',
      features: [
        'AI-assisted MTD ITSA filing',
        'Invoice creation with up to 10 PDF exports/month',
        'Smart expense categorisation',
        'Basic reports (Profit & Loss, income overview)',
        'HMRC integration for direct filing',
        'Standard AI chat access',
        'Email and in-app support'
      ],
      mobileFeatures: [
        'MTD ITSA filing',
        '10 invoices/month',
        'Smart expenses',
        'Basic reports',
        'HMRC integration',
        'AI chat',
        'Email support'
      ],
      notes: [
        'Ideal for sole traders who want simple, smart automation.'
      ],
      cta: {
        type: 'start-trial',
        label: 'Get started free'
      }
    },
    {
      id: 'pro',
      slug: 'pro',
      name: 'Pro',
      description: 'For small businesses and accountants ready for deeper automation.',
      persona: 'Small businesses & accountants',
      price: '£29',
      priceSuffix: '/month',
      tagline: 'Includes a 14-day free trial — scale with richer automations.',
      badge: 'Most Popular',
      includes: 'Includes everything in Starter, plus',
      features: [
        'AI-assisted filing for MTD ITSA, CT600 & VAT',
        'Unlimited invoice creation and PDF exports',
        'Advanced reports (cashflow, forecasting, balance sheet)',
        'Gemini Pro integration for real-time financial research',
        'Priority AI processing queue',
        'Custom automation rules and reconciliation tools',
        'Extended AI message capacity'
      ],
      mobileFeatures: [
        'MTD ITSA, CT600 & VAT',
        'Unlimited invoices',
        'Advanced reports',
        'Gemini Pro AI',
        'Priority processing',
        'Custom automations',
        'Extended AI capacity'
      ],
      notes: [
        'Designed for accountants, bookkeepers, and small companies managing multiple clients.'
      ],
      cta: {
        type: 'start-trial',
        label: 'Start 14-day free trial'
      }
    },
    {
      id: 'pro-plus',
      slug: 'pro-plus',
      name: 'Pro+',
      description: 'Early access for teams that need bank automation.',
      persona: 'Automation-first finance teams',
      price: '£79',
      priceSuffix: '/month',
      tagline: 'Connect bank feeds and automate reconciliations with Neevii AI.',
      includes: 'Everything in Pro, plus',
      features: [
        'Live bank sync (Open Banking)',
        'Auto-reconciliation across connected accounts',
        'Scheduled AI reports and cashflow alerts',
        'API access for external app integrations',
        'Multi-business management (up to 3 entities)',
        'Dedicated AI compute priority'
      ],
      mobileFeatures: [
        'Bank sync',
        'Auto-reconciliation',
        'AI alerts',
        'API access',
        'Up to 3 businesses',
        'AI priority'
      ],
      notes: [
        'Currently in early access. Join the waitlist to be first when live connections open.'
      ],
      cta: {
        type: 'waitlist',
        label: 'Join Waitlist',
        href: '#waitlist'
      }
    },
    {
      id: 'enterprise',
      slug: 'enterprise',
      name: 'Enterprise',
      description: 'Invitation-only program for scaling practices managing multiple clients.',
      persona: 'Accounting practices & high-volume teams',
      pricePrefix: 'From',
      price: '£149',
      priceSuffix: '/month',
      tagline: 'Dedicated support, white-label experiences, and firm-wide automation.',
      includes: 'Everything in Pro+, plus',
      features: [
        'Multi-entity management & consolidated reporting',
        'Custom user roles and Accountant Dashboards',
        'White-label client portal',
        'Priority AI & developer support',
        'Guided AI setup for your first filings',
        'Early access to experimental automation tools'
      ],
      mobileFeatures: [
        'Multi-entity reporting',
        'Custom roles',
        'White-label portal',
        'Priority support',
        'Guided setup',
        'Beta features'
      ],
      notes: [
        'Join the invitation queue to collaborate on roadmap shaping and bespoke integrations.'
      ],
      cta: {
        type: 'waitlist',
        label: 'Talk to Sales',
        href: '#waitlist'
      }
    }
  ];
  const showEnterprise = ((import.meta.env.VITE_ENTERPRISE_ENABLED ?? 'false') === 'true');
  const filteredBasePlans = showEnterprise ? basePlans : basePlans.filter((p) => p?.id !== 'enterprise');

  const starterPlan = filteredBasePlans.find((plan) => plan?.id === 'starter');
  const remainingPlans = filteredBasePlans.filter((plan) => plan?.id !== 'starter');
  const plans = showEarlyAccess
    ? [starterPlan, betaPlan, ...remainingPlans].filter(Boolean)
    : filteredBasePlans;

  const handlePlanSelection = (plan) => {
    if (plan?.cta?.type === 'waitlist') {
      const waitlistSection = document.getElementById('waitlist');
      if (waitlistSection) {
        waitlistSection.scrollIntoView({ behavior: 'smooth' });
      }
      return;
    }

    const baseAppUrl = (import.meta.env.VITE_APP_URL || 'https://app.neevii.com').replace(/\/$/, '');
    const params = new URLSearchParams({
      plan: plan?.slug || plan?.id || 'starter',
      utm_source: 'website',
      utm_campaign: 'pricing_cta',
      utm_content: plan?.slug || plan?.id || 'starter'
    });
    const separator = baseAppUrl.includes('?') ? '&' : '?';
    window.location.href = appendTrackingParams(`${baseAppUrl}${separator}${params.toString()}`);
  };

  // FAQ Schema for rich snippets
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'How does the 14-day free trial work?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Start using Neevii immediately with full access to all features in your chosen plan. No credit card required to begin. After 14 days, choose the tier that fits your workflow or cancel with no charges.'
        }
      },
      {
        '@type': 'Question',
        name: 'Can I change my plan anytime?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Absolutely! You can upgrade or downgrade your plan at any time. When upgrading, you get immediate access to new features. When downgrading, the change takes effect at your next billing cycle.'
        }
      },
      {
        '@type': 'Question',
        name: 'How secure is my financial data?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Your data security is our top priority. We use bank-level 256-bit SSL encryption, are Cyber Essentials certified, and maintain GDPR compliance. Your data is stored in secure, redundant data centers.'
        }
      },
      {
        '@type': 'Question',
        name: 'How accurate is the AI categorization?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Our AI achieves 95%+ accuracy on expense categorization and improves over time as it learns your business patterns. Most users need to manually adjust less than 5% of transactions after the first month.'
        }
      },
      {
        '@type': 'Question',
        name: 'Do you offer refunds?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'We offer a 30-day money-back guarantee on all paid plans. If you are not completely satisfied, contact us within 30 days of your first payment for a full refund.'
        }
      }
    ]
  };

  return (
    <>
      <Seo
        title="Pricing — Neevii"
        description="Transparent pricing for AI-powered accounting. Start a 14-day free trial and upgrade as your practice grows."
        schema={faqSchema}
      />
      <div className="min-h-screen">
      <Header />
      {/* Hero Section */}
      <section className="pt-24 pb-16 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Icon name="Zap" size={16} />
              <span>Transparent Pricing • 14-day free trial</span>
            </div>
            
            <h1 className="text-hero font-bold text-foreground mb-6">
              Choose Your Perfect
              <span className="gradient-text block">Neevii Subscription</span>
            </h1>
            
            <p className="text-body-large text-muted-foreground max-w-3xl mx-auto mb-8">
              Start your 14-day free trial—no credit card required. Every plan includes Neevii’s AI-powered accounting toolkit with HMRC-ready workflows and secure data handling.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-8 mb-12">
            {plans?.map((plan, idx) => (
              <PricingCard
                key={plan?.id}
                plan={plan}
                isPopular={plan?.id === 'beta' ? true : plan?.id === 'pro'}
                onSelectPlan={handlePlanSelection}
              />
            ))}
          </div>

          {/* Trial Reminder */}
          <div className="bg-muted/10 border border-border rounded-2xl p-6 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-foreground uppercase tracking-wide mb-2">Trial Included</p>
              <p className="text-muted-foreground max-w-3xl">
                Every Neevii plan starts with a 14-day free trial. Explore AI-powered filings, reporting, and automation before you commit.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Customer Success Stories */}
      <section className="py-16 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Real Results from Real Customers
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              See how businesses like yours are saving time and money with Neevii.
            </p>
          </div>

          <div className="bg-card rounded-2xl border border-border p-10 text-center max-w-4xl mx-auto">
            <p className="text-lg font-semibold text-foreground mb-3">
              Neevii is new — and growing fast. Our early adopters are helping us shape the future of AI accounting.
            </p>
            <p className="text-muted-foreground">
              Be one of the first to join and leave your mark.
            </p>
          </div>
        </div>
      </section>
      {/* Feature Comparison */}
      <section className="py-16 px-6 lg:px-8 bg-muted/5">
        <div className="max-w-7xl mx-auto">
          <FeatureComparison plans={plans} />
        </div>
      </section>
      {/* Coming Soon */}
      <section id="coming-soon" className="py-16 px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="bg-card rounded-2xl border border-border p-8">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div>
                <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-3 py-1 rounded-full text-xs font-semibold mb-4">
                  <Icon name="Activity" size={14} />
                  <span>Coming Soon</span>
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-3">Live Open Banking integration</h3>
                <p className="text-muted-foreground max-w-3xl">
                  Launching first for Pro+ users. Securely connect bank feeds, enable real-time reconciliation, and stream transactions directly into Neevii workflows.
                </p>
              </div>
              <Button
                variant="outline"
                iconName="Bell"
                iconPosition="left"
                onClick={() => {
                  window.location.href = '#waitlist';
                }}
              >
                Join Pro+ Waitlist
              </Button>
            </div>
          </div>
          <div className="mt-8">
            <ProPlusWaitlistForm />
          </div>
        </div>
      </section>
      {/* Feature Glossary */}
      <section id="glossary" className="py-16 px-6 lg:px-8 bg-muted/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-foreground mb-3">Feature Glossary</h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Understand how Neevii’s premium capabilities help firms scale securely without sacrificing client experience.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-card rounded-2xl border border-border p-6">
              <h3 className="text-xl font-semibold text-foreground mb-3">Custom User Roles & Accountant Dashboards</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Provide accountants with a portfolio-style view of multiple client businesses. Control access with simple roles such as Viewer, Editor, or Submitter so every collaborator has the right permissions.
              </p>
            </div>
            <div className="bg-card rounded-2xl border border-border p-6">
              <h3 className="text-xl font-semibold text-foreground mb-3">White-Label Client Portal</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Offer clients a branded login powered by Neevii. Deliver firm-branded dashboards while Neevii handles compliance, security, and filing workflows under the hood.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Security Summary */}
      <section className="py-16 px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="bg-card rounded-2xl border border-border p-10 shadow-brand">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-foreground mb-3">
                How Neevii Keeps Your Data Safe
              </h2>
              <p className="text-muted-foreground">
                Launching with security-first fundamentals so you can onboard early with confidence.
              </p>
            </div>
            <ul className="space-y-4 text-left">
              {[
                'Sign in with Google — no passwords stored by Neevii',
                'Payments by Stripe — we never see or store card details; Google Pay/Apple Pay supported',
                'CSV-only to start — upload statements securely; files are processed then discarded',
                'Your data is isolated with Row Level Security — only you can access your records',
                'Encrypted in transit (HTTPS/TLS) and at rest (managed Postgres)',
                'UK data residency — hosted in London (eu-west-2)'
              ]?.map((item) => (
                <li key={item} className="flex items-start space-x-3">
                  <Icon name="Shield" size={18} className="text-accent mt-0.5" />
                  <span className="text-sm text-muted-foreground leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground mt-8 italic">
              Open Banking connections — coming soon.
            </p>
          </div>
        </div>
      </section>
      {/* FAQ Section */}
      <section className="py-16 px-6 lg:px-8 bg-muted/5">
        <div className="max-w-4xl mx-auto">
          <FAQ />
        </div>
      </section>
      {/* Footer */}
      <footer className="py-12 px-6 lg:px-8 border-t border-border">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 gap-8">
            {/* Company Info */}
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 bg-gradient-primary rounded-lg flex items-center justify-center">
                  <Icon name="Zap" size={20} className="text-primary-foreground" />
                </div>
                <span className="text-lg font-bold text-foreground">Neevii</span>
              </div>
              <p className="text-muted-foreground mb-4 max-w-md">
                AI-powered accounting that amplifies your intelligence and simplifies your financial management.
              </p>
              <div className="flex items-center space-x-4">
                <a href="#" className="text-muted-foreground hover:text-accent transition-colors">
                  <Icon name="Twitter" size={20} />
                </a>
                <a href="#" className="text-muted-foreground hover:text-accent transition-colors">
                  <Icon name="Linkedin" size={20} />
                </a>
                <a href="#" className="text-muted-foreground hover:text-accent transition-colors">
                  <Icon name="Youtube" size={20} />
                </a>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center">
              <p className="text-muted-foreground text-sm">
                © {new Date()?.getFullYear()} Neevii. All rights reserved.
            </p>
            <div className="flex items-center space-x-6 mt-4 md:mt-0">
              <Link to="/faq" className="text-muted-foreground hover:text-accent transition-colors text-sm">
                FAQ
              </Link>
              <Link to="/facts-for-ai" className="text-muted-foreground hover:text-accent transition-colors text-sm">
                Facts for AI
              </Link>
              <Link to="/privacy" className="text-muted-foreground hover:text-accent transition-colors text-sm">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-muted-foreground hover:text-accent transition-colors text-sm">
                Terms of Service
              </Link>
              <a href="#" className="text-muted-foreground hover:text-accent transition-colors text-sm">
                Cookie Policy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
};

export default SubscriptionPage;